import PropTypes from 'prop-types';
import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_CONTENT =
  'The qualifications listed are those by virtue of which the oral health practitioner was registered. This is not a comprehensive list of the practitioner’s qualifications, training and experience';

const TOOLTIP_BG = 'rgba(0, 0, 0, 0.9)';
const ARROW_BASE = '5px';
const TRIGGER_GAP_PX = 5;

/**
 * Lightweight hover/focus tooltip
 */
const InlineTooltip = ({ children, content = DEFAULT_CONTENT }) => {
  const tooltipId = useId();
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const syncPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.top + r.height / 2, left: r.right + TRIGGER_GAP_PX });
  }, []);

  const show = () => {
    syncPosition();
    setOpen(true);
  };

  const hide = () => setOpen(false);

  useLayoutEffect(() => {
    if (!open) return;
    syncPosition();
    const onScrollResize = () => syncPosition();
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [open, syncPosition]);

  useLayoutEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const bubble =
    open && typeof document !== 'undefined' ? (
      <span
        id={tooltipId}
        role="tooltip"
        style={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          transform: 'translateY(-50%)',
          zIndex: 1070,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          pointerEvents: 'none',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: `${ARROW_BASE} ${ARROW_BASE} ${ARROW_BASE} 0`,
            borderTopColor: 'transparent',
            borderRightColor: TOOLTIP_BG,
            borderBottomColor: 'transparent',
            borderLeftColor: 'transparent',
            flexShrink: 0,
          }}
        />
        <span
          style={{
            minWidth: 200,
            maxWidth: 'min(276px, calc(100vw - 40px))',
            padding: '3px 8px',
            fontSize: 12,
            lineHeight: 1.428571429,
            fontWeight: 'normal',
            fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
            color: '#fff',
            backgroundColor: TOOLTIP_BG,
            borderRadius: '4px',
            boxSizing: 'border-box',
          }}
        >
          {content}
        </span>
      </span>
    ) : null;

  return (
    <>
      <span ref={triggerRef} style={{ display: 'inline-block' }} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
        <a href="#" className="no-link" aria-describedby={open ? tooltipId : undefined} onClick={(event) => event.preventDefault()}>
          {children}
        </a>
      </span>
      {bubble && createPortal(bubble, document.body)}
    </>
  );
};

InlineTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  content: PropTypes.string,
};

export default InlineTooltip;
